<template>
  <div>
    <spinner-round
      :is-loading="true"
      v-if="!rawStartDate"></spinner-round>
    <div v-else>
      <tabs
        :links="statsTabs"
        :level="1"
        class="columns is-sticky-in-mainColumn is-zindex-8"></tabs>
      <div
        v-if="$route.name === 'StatsProgramSales'"
        class="has-background-white is-border is-margin-top">
        <div class="columns is-gapless">
          <div class="column is-3 is-border-right">
            <date-picker
              id="startDatePicker"
              :model="rawStartDate"
              :clearable="false"
              :field-class="'is-padding-left is-padding-right'"
              :input-class="'is-fullwidth is-borderless has-background-transparent'"
              @input="updateDateFilter({ start: $event })"></date-picker>
          </div>
          <div class="column is-3 is-border-right">
            <date-picker
              id="endDatePicker"
              :model="rawEndDate"
              :clearable="false"
              :field-class="'is-padding-left is-padding-right'"
              :input-class="'is-fullwidth is-borderless has-background-transparent'"
              @input="updateDateFilter({ end: $event })"></date-picker>
          </div>
          <div class="column is-3 is-padding-left is-padding-right">
            <div class="select is-fullwidth has-background-transparent">
              <select
                id="period"
                v-model="periodSelected"
                class="is-borderless is-shadowless">
                <option :value="null">
                  Périodes
                </option>
                <option
                  v-for="period in periodsDatas"
                  :value="period"
                  :key="'period' + period.value">
                  {{ period.label }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div
        class="is-padding-top-small is-padding-bottom-small is-padding-left-small is-border-left is-border-right"
        v-if="$route.name === 'StatsProgramSales'">
        <div class="field is-grouped">
          <p class="control">
            <label
              v-for="p in availableSaleStatusList"
              :for="'statusFilter-' + p.value"
              :key="p.value"
              :id="'status-' + p.value"
              class="checkbox checkRadioIsHide is-margin-right-xsmall">
              <input
                type="checkbox"
                v-model="filters.status"
                :id="'statusFilter-' + p.value"
                :value="p.value"
                :name="'status-' + p.value"
                @change="getStats()">
              <span
                class="tag"
                :class="p.value === 'canceled' ? 'is-danger' : 'is-' + p.value">
                <span>{{ p.label }}</span>
                <span class="icon">
                  <i
                    class="far"
                    :class="filters.status && filters.status.includes(p.value) ? 'fa-check-square' : 'fa-square'"></i>
                </span>
              </span>
            </label>
          </p>
        </div>
      </div>
      <stats-sales-graph
        v-if="$route.name === 'StatsProgramSales'"
        :from="'Program'"
        :program-options="programOptions"
        :total-sales="totalSales"
        :stats-sales="sortedSales"
        :stats-reference-sales="statsReferenceSales"
        :filters="filters"
        :reference-selected="referenceSelected"
        :total-reference-sales="totalReferenceSales"
        :reference-datas="referenceDatas"
        :flow-rate="flowRate"
        @updateFilters="updateFilters"
        @updateReference="updateReference"></stats-sales-graph>
      <router-view
        :key="$route.name"
        :program-options="programOptions"
        :program-contacts="program.contacts"
        :source-stats-sales-pre-reservation="sortedSales.filter(x => x.status === 'pre_reserved')"
        :source-stats-sales-reservation="sortedSales.filter(x => x.status === 'reserved')"
        :source-stats-sales-sold="sortedSales.filter(x => x.status === 'sold')"
        :source-stats-sales-canceled="sortedSales.filter(x => x.status === 'canceled')"
        :source-stats-reference-sales-pre-reservation="statsReferenceSalesPreReservation"
        :source-stats-reference-sales-reservation="statsReferenceSalesReservation"
        :source-stats-reference-sales-sold="statsReferenceSalesSold"
        :source-stats-rdvs="statsRdvs"
        :source-flow-rate="flowRate"
        :source-stats-multidiffusion="statsMultidiffusion"
        :source-loading="loading"
        :source-sorted-params="sortedParams"
        @sort="sort"
        @updateStats="getStats"
        @updateSources="updateSources"></router-view>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'
import StatsMixins from '@/components/statistics/StatsMixins'

export default {
  name: 'ProgramStats',
  components: {
    StatsSalesGraph: () => import('@/components/statistics/StatsSales/StatsSalesGraph')
  },
  mixins: [StatsMixins],
  props: {
    programOptions: {
      type: Array
    },
    program: {
      required: true
    }
  },
  data () {
    return {
      waitForDateSet: true,
      rawStartDate: null,
      rawEndDate: moment().endOf('day').toDate()
    }
  },
  computed: {
    ...mapGetters(['accountPk']),
    ...mapState(['currentUser']),
    currentStatsId () {
      return this.currentUser.account.pk
    },
    statsDefaultParams () {
      return {
        program: [this.program.pk],
        account: this.accountPk
      }
    },
    availableSaleStatusList () {
      return [
        { value: 'pre_reserved', label: 'Pré-Réservation' },
        { value: 'reserved', label: 'Réservation' },
        { value: 'sold', label: 'Acte' },
        { value: 'canceled', label: 'Annulation' }
      ]
    },
    totalSales () {
      if (!this.statsSales || !this.statsSales.length) return {}
      return this.processTotalSales(this.statsSales)
    },
    statsTabs () {
      let tabs = [
        { link: 'StatsProgramSales', label: 'Réservations/Ventes', icon: 'fas fa-medal' },
        { link: 'ProgramOptions', label: 'Options', icon: 'fas fa-user-clock' },
        { link: 'ProgramPromoteurContacts', label: 'Contacts', icon: 'fas fa-user' }
      ]
      return tabs
    }
  },
  watch: {
    periodSelected (v) {
      this.updateDateFilter(v)
    }
  },
  mounted () {
    this.getFlowRate()
  },
  created () {
    if (!this.statsTabs.map(x => x.link).includes(this.$route.name)) {
      this.$router.replace({ name: 'StatsProgramSales' }).catch(() => {})
    }
    this.getStatsStartDate()
  },
  methods: {
    async getStatsStartDate () {
      let params = {
        ...this.statsDefaultParams
      }
      try {
        const { data } = await this.$statsApi.get(`/sales`, { params })
        if (data.length > 1) {
          let allDates = []
          for (let item of data) {
            if (item.pre_reservation_date) {
              allDates.push(item.pre_reservation_date)
            }
            if (item.signature_date) {
              allDates.push(item.signature_date)
            }
            if (item.sale_date) {
              allDates.push(item.sale_date)
            }
          }
          allDates = allDates.sort()
          this.rawStartDate = moment(allDates[0]).startOf('day').toDate()
        } else {
          this.rawStartDate = moment().startOf('year').toDate()
        }
      } catch (err) {
        console.log(err)
      } finally {
        this.waitForDateSet = false
      }
    },
    getStats (referenceMode) {
      if (!this.accountPk) return
      if (this.$route.name === 'StatsProgramSales') {
        this.getStatsSales(referenceMode)
      }
    }
  }
}
</script>
